import { Updater } from "use-immer";

import { IgAnalyticsTooltip } from "@intelligems/ig-theme/IgAnalyticsTooltip";
import { IgCheckbox } from "@intelligems/ig-theme/IgCheckbox";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { FormControlLabel, Stack, useTheme } from "@mui/material";

interface TimeSeriesDrawerContentProps {
  cumulative: boolean;
  setCumulative: Updater<boolean>;
  confidenceInterval: boolean;
  setConfidenceInterval: Updater<boolean>;
  isLoading: boolean;
  hasNoData: boolean;
  hasConfidenceIntervalData: boolean;
}

export function TimeSeriesDrawerContent({
  cumulative,
  setCumulative,
  confidenceInterval,
  setConfidenceInterval,
  isLoading,
  hasNoData,
  hasConfidenceIntervalData,
}: TimeSeriesDrawerContentProps) {
  const theme = useTheme();

  const disableConfidenceInterval =
    isLoading || hasNoData || !cumulative || !hasConfidenceIntervalData;

  return (
    <Stack spacing={3} sx={{ px: 3, pt: 2, pb: 2 }}>
      <Stack spacing={2}>
        {/* Cumulative checkbox */}
        <IgAnalyticsTooltip
          title={
            hasNoData
              ? "Cumulative checkbox is disabled when no data is returned"
              : isLoading
                ? "Cumulative checkbox is disabled while data is loading"
                : ""
          }
        >
          <FormControlLabel
            disabled={isLoading || hasNoData}
            control={
              <IgCheckbox
                disabled={isLoading || hasNoData}
                name="cumulative"
                checked={cumulative}
                size="small"
                onChange={(e) => {
                  if (!e.target.checked) {
                    setConfidenceInterval(false);
                  }
                  setCumulative((draft) => {
                    draft = !draft;
                    return draft;
                  });
                }}
              />
            }
            label={
              <IgTypography
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 600,
                }}
                variant="body2"
              >
                Cumulative
              </IgTypography>
            }
          />
        </IgAnalyticsTooltip>

        {/* Confidence Interval checkbox */}
        <IgAnalyticsTooltip
          title={
            hasNoData
              ? "Confidence Interval checkbox is disabled when no data is returned"
              : isLoading
                ? "Confidence Interval checkbox disabled while data is loading"
                : !cumulative
                  ? "Enable cumulative to show confidence intervals"
                  : !hasConfidenceIntervalData
                    ? "No confidence interval data available"
                    : ""
          }
        >
          <FormControlLabel
            disabled={disableConfidenceInterval}
            control={
              <IgCheckbox
                name="confidenceIntervals"
                size="small"
                checked={confidenceInterval}
                onClick={() => {
                  setConfidenceInterval((draft) => {
                    draft = !draft;
                    return draft;
                  });
                }}
              />
            }
            label={
              <IgTypography
                sx={{
                  color: disableConfidenceInterval
                    ? theme.palette.text.disabled
                    : theme.palette.text.secondary,
                  fontWeight: 600,
                }}
                variant="body2"
              >
                Confidence Intervals
              </IgTypography>
            }
          />
        </IgAnalyticsTooltip>
      </Stack>
    </Stack>
  );
}
